import React from "react";
import { Handle, Position } from "@xyflow/react";
import { useTranslation } from "react-i18next";
import { HoverTooltip } from "../../common/HoverTooltip";
import { getTechnologyLogo } from "../../common/technologyLogo";

// Technology initials, e.g. "EarthRanger" -> "ER", "SMART Connect" -> "SC".
const initials = (name?: string) =>
    (name || "?").split(/[\s-]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

// The card's height is fixed and mirrored by NODE_H, which React Flow is given as
// the node's declared height: the handles below sit at 35%/65% of it, so a card
// with a URL line and one without still connect at the same heights, and the map
// never has to wait for a measurement to show a node.
const AccountNode = ({ data }: any) => {
    const { t } = useTranslation();
    const logo = getTechnologyLogo(data.techValue);
    return (
        <HoverTooltip content={data.needsSetup ? t("Configure Integration — setup needed") : t("Configure Integration")} position="top" portal focusable={false} className="block">
        <div className={`border rounded-lg bg-white px-4 py-3 w-[260px] h-[100px] cursor-pointer transition-shadow hover:shadow-md ${
            data.isSelected ? "border-route-green ring-2 ring-route-green" : "border-gray-lines"}`}>
            <div className="flex items-center gap-2">
                {/* The technology's own logo where there is one; initials are the
                    fallback so every card keeps the same footprint. */}
                {logo
                    ? <img src={logo} alt="" aria-hidden="true" className="flex-none w-7 h-7 object-contain" />
                    : <span className="flex-none w-7 h-7 rounded bg-light-green text-route-green text-[11px] font-bold flex items-center justify-center">
                        {initials(data.techName)}
                      </span>}
                <span className="text-off-black font-semibold truncate" title={data.name}>{data.name}</span>
                {/* checklist state: every required field saved (✓) or setup pending (!) */}
                <span className={`ml-auto flex-none w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center ${
                    data.needsSetup ? "bg-orange-50 text-orange border border-orange-200" : "bg-light-green text-route-green"}`}
                    aria-label={data.needsSetup ? t("Needs setup — required fields are missing") : t("Configured")}>
                    {data.needsSetup ? "!" : "✓"}
                </span>
            </div>
            {data.base_url && <div className="text-xs text-secondary-text truncate mt-1" title={data.base_url}>{data.base_url}</div>}
            <div className="flex items-center gap-2 mt-1.5">
                {/* min-w-0 + truncate keeps a long role label on one line, and the
                    pill never wraps — the card height stays fixed. */}
                <span className="min-w-0 truncate text-[11px] uppercase tracking-wider text-medium-gray font-semibold">
                    {data.roleLabel}
                </span>
                {data.techName && (
                    <span className="flex-none whitespace-nowrap bg-light-green text-route-green rounded-full px-2 py-0.5 text-[11px] font-semibold truncate max-w-[120px]" title={data.techName}>
                        {data.techName}
                    </span>
                )}
            </div>

            {data.isProvider ? (
                <>
                    <Handle type="source" id="out-source" position={Position.Right} isConnectable={false}
                        className="opacity-0" style={{ top: "35%" }} />
                    <Handle type="target" id="in-target" position={Position.Right} isConnectable={false}
                        className="opacity-0" style={{ top: "65%" }} />
                </>
            ) : (
                <>
                    <Handle type="target" id="out-target" position={Position.Left} isConnectable={false}
                        className="opacity-0" style={{ top: "35%" }} />
                    <Handle type="source" id="in-source" position={Position.Left} isConnectable={false}
                        className="opacity-0" style={{ top: "65%" }} />
                </>
            )}
        </div>
        </HoverTooltip>
    );
};

export default AccountNode;
